import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTable, useFlexLayout } from "react-table";

// FlexTable — a reusable react-table renderer styled to match the app's table cards: an
// uppercase header row, soft zebra-striped rounded rows, a hover highlight, and per-column
// alignment driven by each column def's `align` ("left" by default, or "center"). It is
// designed to sit flush inside a TableCard body (it draws no border of its own).
//
// Column alignment: add `align: "center"` or `align: "right"` to any react-table column
// definition (v7 merges custom fields onto the column instance) instead of hardcoding ids.
export default function FlexTable({
	columns,
	data,
	onRowClick,
	getRowId = (row) => row.id,
	rowAriaLabel,
	rowHeight = null,
	selectedRowId = null,
}) {
	const defaultColumn = useMemo(
		() => ({ minWidth: 30, width: 150, maxWidth: 800 }),
		[]
	);

	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
		useTable({ columns, data, defaultColumn }, useFlexLayout);

	const clickable = typeof onRowClick === "function";

	return (
		<>
			<style>{`.flex-table, .flex-table * { box-sizing: border-box; }`}</style>
			<div
				{...getTableProps()}
				className="flex-table flex w-full cursor-default flex-auto flex-col"
			>
				{/* Header — padding matches the body column-for-column so values line up. */}
				<div className="py-2 text-text-2">
					{headerGroups.map((headerGroup) => {
						const { key, ...headerGroupProps } =
							headerGroup.getHeaderGroupProps();
						return (
							<div key={key} {...headerGroupProps} className="tr">
								{headerGroup.headers.map((column, colIndex) => {
									const { key: colKey, ...colProps } =
										column.getHeaderProps();
									return (
										<div
											key={colKey}
											{...colProps}
											className={`th flex items-center text-[11px] font-medium uppercase tracking-wide ${
												column.align === "center"
													? "justify-center text-center"
													: column.align === "right"
														? "justify-end pr-5 text-right"
														: `text-left ${colIndex === 0 ? "pl-5" : "pl-2"}`
											}`}
										>
											{column.render("Header")}
										</div>
									);
								})}
							</div>
						);
					})}
				</div>

				{/* Body — sits flush inside the card (no inner border). */}
				<div {...getTableBodyProps()}>
					{rows.map((row, index) => {
						prepareRow(row);
						const isLast = index === rows.length - 1;
						// Zebra striping: nth-child(even), i.e. 0-based odd index, gets a soft
						// rounded pill; hover sits a touch darker.
						const isEven = index % 2 === 1;
						const { key, style: rowStyle, ...rowProps } =
							row.getRowProps();
						const rowId = getRowId(row.original);
						// selectedRowId (optional) highlights the currently-open row — used by the
						// board-report driver table's per-row selection. null (the default) → no
						// highlight, so every existing caller renders unchanged.
						const isSelected = selectedRowId != null && rowId === selectedRowId;
						return (
							<div
								key={key}
								{...rowProps}
								style={rowHeight != null ? { ...rowStyle, height: rowHeight } : rowStyle}
								className={`tr group rounded-[11px] transition-colors hover:bg-[#EAEEF5] ${
									isSelected ? "bg-[#DCE6F7]" : isEven ? "bg-[#F7F9FC]" : ""
								} ${isLast ? "" : "mb-[3px]"} ${clickable ? "cursor-pointer" : ""}`}
								onClick={clickable ? (e) => onRowClick(rowId, row.original, e) : undefined}
								onKeyDown={
									clickable
										? (e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													onRowClick(rowId, row.original, e);
												}
											}
										: undefined
								}
								tabIndex={clickable ? 0 : undefined}
								role={clickable ? "button" : undefined}
								aria-pressed={selectedRowId != null && clickable ? isSelected : undefined}
								aria-label={rowAriaLabel ? rowAriaLabel(row.original) : undefined}
							>
								{row.cells.map((cell, cellIndex) => {
									const { key: cellKey, ...cellProps } =
										cell.getCellProps();
									return (
										<div
											key={cellKey}
											{...cellProps}
											className={`td flex items-center py-2.5 text-sm ${
												cell.column.align === "center"
													? "justify-center"
													: cell.column.align === "right"
														? "justify-end pr-5"
														: cellIndex === 0 ? "pl-5" : "pl-2"
											}`}
										>
											{cell.render("Cell")}
										</div>
									);
								})}
							</div>
						);
					})}
				</div>
			</div>
		</>
	);
}

FlexTable.propTypes = {
	columns: PropTypes.array.isRequired,
	data: PropTypes.array.isRequired,
	onRowClick: PropTypes.func,
	getRowId: PropTypes.func,
	rowAriaLabel: PropTypes.func,
	rowHeight: PropTypes.number,
	selectedRowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
